import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AdminContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, Package, Wrench, Image, Star, Gift, MessageSquare,
  Home, Info, Phone, Palette, Search, Users, Settings, LogOut, Menu, X,
  ChevronDown, Bell, User, ExternalLink, FolderOpen, Video, Building2, Upload
} from 'lucide-react';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { id: 'divider1', type: 'divider', label: 'Content Management' },
  { id: 'homepage', label: 'Homepage', icon: Home, path: '/admin/homepage' },
  { id: 'about', label: 'About Us', icon: Info, path: '/admin/about' },
  { id: 'categories', label: 'Categories', icon: FolderOpen, path: '/admin/categories' },
  { id: 'services', label: 'Services', icon: Wrench, path: '/admin/services' },
  { id: 'products', label: 'Products', icon: Package, path: '/admin/products' },
  { id: 'projects', label: 'Gallery', icon: Image, path: '/admin/projects' },
  { id: 'reels', label: 'Reels & Videos', icon: Video, path: '/admin/reels' },
  { id: 'reviews', label: 'Reviews', icon: Star, path: '/admin/reviews' },
  { id: 'client-logos', label: 'Client Logos', icon: Building2, path: '/admin/client-logos' },
  { id: 'offers', label: 'Offers', icon: Gift, path: '/admin/offers' },
  { id: 'image-upload', label: 'Image Uploader', icon: Upload, path: '/admin/image-upload' },
  { id: 'divider2', type: 'divider', label: 'Business' },
  { id: 'enquiries', label: 'Enquiries', icon: MessageSquare, path: '/admin/enquiries', badge: true },
  { id: 'contact', label: 'Contact Info', icon: Phone, path: '/admin/contact' },
  { id: 'divider3', type: 'divider', label: 'Appearance' },
  { id: 'branding', label: 'Branding', icon: Palette, path: '/admin/branding' },
  { id: 'seo', label: 'SEO', icon: Search, path: '/admin/seo' },
  { id: 'divider4', type: 'divider', label: 'System' },
  { id: 'users', label: 'Users', icon: Users, path: '/admin/users' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
];

export const AdminLayout = ({ children, stats = {} }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-slate-900 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} hidden lg:block`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold font-['Oswald'] flex-shrink-0">
              R
            </div>
            {sidebarOpen && (
              <div className="text-white">
                <h1 className="font-['Oswald'] font-bold text-sm leading-tight">RAJ ALUMINUM</h1>
                <p className="text-[10px] text-slate-400">Admin Panel</p>
              </div>
            )}
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white p-1">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {menuItems.map((item) => {
            if (item.type === 'divider') {
              return sidebarOpen ? (
                <div key={item.id} className="pt-4 pb-2">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-3">{item.label}</p>
                </div>
              ) : <div key={item.id} className="py-2 border-t border-slate-800 my-2" />;
            }

            return (
              <Link
                key={item.id}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive(item.path)
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.badge && stats.new_enquiries > 0 && (
                      <span className="ml-auto px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {stats.new_enquiries}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold font-['Oswald']">
            R
          </div>
          <div className="text-white">
            <h1 className="font-['Oswald'] font-bold text-sm">RAJ ALUMINUM</h1>
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2">
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-slate-900 pt-16 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              if (item.type === 'divider') {
                return (
                  <div key={item.id} className="pt-4 pb-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3">{item.label}</p>
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-sky-500 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge && stats.new_enquiries > 0 && (
                    <span className="ml-auto px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                      {stats.new_enquiries}
                    </span>
                  )}
                </Link>
              );
            })}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-red-400 hover:bg-slate-800 transition-all mt-4"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        {/* Top Bar */}
        <header className="hidden lg:flex h-16 bg-white border-b items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <h2 className="font-['Oswald'] text-lg font-semibold text-slate-900">
              {menuItems.find(m => isActive(m.path) && m.path !== '/admin')?.label || 'Dashboard'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
              <ExternalLink className="w-4 h-4" />
              View Site
            </Link>
            <div className="flex items-center gap-2 pl-4 border-l">
              <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{user?.name || user?.username}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
